import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, RefreshCw, Hammer, Check, CalendarDays } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FormatIcon } from "@/lib/content";
import { CardLimitModal } from "@/components/UpgradeModal";

const monday = () => {
  const d = new Date(); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
};

function Locked() {
  const nav = useNavigate();
  return (
    <div className="relative" data-testid="planner-locked">
      <div className="space-y-3 blur-[3px] opacity-50 pointer-events-none select-none">
        {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((d) => (
          <div key={d} className="panel p-5"><p className="eyebrow">{d}</p><p className="quote text-lg mt-2">“A line in her voice, ready to build…”</p></div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="panel p-8 text-center max-w-sm border-[#9C4DDC]/60">
          <Lock className="mx-auto text-[#9C4DDC] mb-4" strokeWidth={1.5} />
          <p className="font-display text-2xl">The Weekly Planner is a Pro feature</p>
          <p className="muted text-sm mt-2">Plan a full week of on-brand posts in one tap.</p>
          <button className="btn-primary w-full mt-6" onClick={() => nav("/pricing")} data-testid="planner-upgrade-button">Upgrade to Pro</button>
        </div>
      </div>
    </div>
  );
}

function SlotRow({ slot, idx, fmt, onSave, onSwap, onBuild }) {
  const [quote, setQuote] = useState(slot.quote);
  const [busy, setBusy] = useState("");
  useEffect(() => setQuote(slot.quote), [slot.quote]);
  const run = (k, fn) => async () => { setBusy(k); try { await fn(); } finally { setBusy(""); } };
  const built = slot.card_id;
  return (
    <div className="panel p-5 fade-up" style={{ animationDelay: `${idx * 50}ms` }} data-testid={`planner-slot-${idx}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <p className="eyebrow">{slot.day} <span className="muted normal-case tracking-normal ml-1">{slot.date?.slice(5)}</span></p>
          <p className="text-xs muted mt-1 flex items-center gap-1.5"><FormatIcon icon={fmt?.icon} size={13} className="text-[#9C4DDC]" />{fmt?.name} · {slot.goal}</p>
        </div>
        {built && <span className="text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full bg-[#9C4DDC] text-white flex items-center gap-1" data-testid={`planner-slot-built-${idx}`}><Check size={10} /> {slot.card_state === "done" ? "Built" : "Building"}</span>}
      </div>
      <textarea className="field quote text-base min-h-[80px]" value={quote} onChange={(e) => setQuote(e.target.value)}
        onBlur={() => quote.trim() && quote !== slot.quote && onSave(idx, quote)} data-testid={`planner-slot-quote-${idx}`} />
      <div className="flex gap-2 mt-3">
        <button className="btn-secondary py-2 px-4 flex-1" disabled={!!busy} onClick={run("swap", () => onSwap(idx))} data-testid={`planner-slot-swap-${idx}`}>
          {busy === "swap" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Swap idea
        </button>
        <button className="btn-primary py-2 px-4 flex-1" disabled={!!busy} onClick={run("build", () => (built ? onBuild(idx, true) : onBuild(idx, false, quote)))} data-testid={`planner-slot-build-${idx}`}>
          {busy === "build" ? <Loader2 size={14} className="animate-spin" /> : <Hammer size={14} />} {built ? "Open card" : "Approve & build"}
        </button>
      </div>
    </div>
  );
}

export default function Planner() {
  const { user } = useAuth();
  const nav = useNavigate();
  const pro = user.plan === "pro";
  const [muses, setMuses] = useState([]);
  const [formats, setFormats] = useState([]);
  const [form, setForm] = useState({ muse_id: "", week_start: monday(), posts_per_week: 7, theme: "" });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(false);

  useEffect(() => {
    api.get("/muses").then(({ data }) => setMuses([...data.muses, ...(data.example ? [data.example] : [])]));
    api.get("/formats").then(({ data }) => setFormats(data));
    if (pro) api.get("/plans").then(({ data }) => data[0] && setPlan(data[0]));
  }, [pro]);

  const fmt = Object.fromEntries(formats.map((f) => [f.key, f]));
  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/plans", form); setPlan(data); toast.success("Your week is planned"); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };
  const save = async (idx, quote) => {
    try { const { data } = await api.patch(`/plans/${plan.id}/slots/${idx}`, { quote }); setPlan(data); toast.success("Quote saved"); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const swap = async (idx) => {
    try { const { data } = await api.post(`/plans/${plan.id}/slots/${idx}/swap`); setPlan(data); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const build = async (idx, open, quote) => {
    const s = plan.slots[idx];
    if (open) return nav(`/library/${s.card_id}`);
    try {
      const { data } = await api.post("/cards", { muse_id: plan.muse_id, format_key: s.format_key, topic: s.topic, quote, plan_id: plan.id, slot_index: idx });
      nav(`/library/${data.id}`);
    } catch (err) {
      if (["CARD_LIMIT_REACHED", "MONTHLY_CAP_REACHED"].includes(err?.response?.data?.detail)) setLimit(true);
      else toast.error(formatErr(err));
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-3xl">
      <p className="eyebrow mb-3 flex items-center gap-2"><CalendarDays size={12} /> Weekly Planner</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl mb-10">Plan her week.</h1>
      {!pro ? <Locked /> : (
        <>
          <div className="panel p-5 md:p-6 space-y-5 mb-10" data-testid="planner-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label">Muse</label>
                <select className="field" value={form.muse_id} onChange={set("muse_id")} data-testid="planner-muse-select">
                  <option value="">Choose a muse</option>{muses.map((m) => <option key={m.id} value={m.id}>{m.name}{m.is_example ? " (example)" : ""}</option>)}
                </select></div>
              <div><label className="label">Week starting</label>
                <input type="date" className="field" value={form.week_start} onChange={set("week_start")} data-testid="planner-week-start-input" /></div>
            </div>
            <div><label className="label">Posts per week</label>
              <div className="flex gap-2">{[3, 5, 7].map((n) => (
                <button key={n} type="button" onClick={() => setForm({ ...form, posts_per_week: n })} data-testid={`planner-posts-${n}`}
                  className={`flex-1 py-3 rounded-[16px] border text-sm transition-colors ${form.posts_per_week === n ? "bg-[#9C4DDC] text-white border-[#9C4DDC]" : "border-[#F0EAF7] muted"}`}>{n}</button>
              ))}</div></div>
            <div><label className="label">Theme or goal (optional)</label>
              <input className="field" value={form.theme} onChange={set("theme")} placeholder="e.g. launch my free kit" data-testid="planner-theme-input" /></div>
            <button className="btn-primary w-full py-4" disabled={!form.muse_id || busy} onClick={generate} data-testid="planner-generate-button">
              {busy ? <><Loader2 size={16} className="animate-spin" /> Planning…</> : "Generate my week"}
            </button>
          </div>
          {plan && (
            <div data-testid="planner-plan">
              <div className="flex items-end justify-between mb-5">
                <div><h2 className="text-base md:text-lg font-display">Week of {plan.week_start}</h2>{plan.theme && <p className="text-xs muted mt-1">{plan.theme}</p>}</div>
                <span className="text-xs text-[#9C4DDC]" data-testid="planner-progress">{plan.built}/{plan.slots.length} built</span>
              </div>
              <div className="space-y-3">
                {plan.slots.map((s, i) => <SlotRow key={`${plan.id}-${i}`} slot={s} idx={i} fmt={fmt[s.format_key]} onSave={save} onSwap={swap} onBuild={build} />)}
              </div>
            </div>
          )}
        </>
      )}
      <CardLimitModal open={limit} onOpenChange={setLimit} />
    </div>
  );
}
